import type { FC } from "react";
import React, { useCallback, useState } from "react";
import { validateEmail } from "@components/Auth/Form/Email";
import { validatePassword } from "@components/Auth/Form/Password";
import { FormLink } from "@components/AuthFlow/FormLink";
import ModalMessage from "@components/AuthFlow/Helpers/ModalMessage";
import AuthInputGroup from "@components/AuthFlow/Signin/AuthInputGroup";
import SubmitButton, {
  SIGNIN_SUBMIT_BUTTON,
} from "@components/AuthFlow/Signin/SubmitButton";
import { trackError, withPrefix } from "@tracking/tracking";
import { useEdoLogin } from "clientState/AuthFlow/hooks";
import {
  screenNames,
  useScreenState,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { useTracking, useTranslation } from "@edreamsodigeo/frontend-core";
import { Box, Flex } from "@edreamsodigeo/prisma-design-system";

import type { SigninFormProps } from "./contract";
import { EDO_LOGIN_ERROR_ID, LOGIN_RESET_PASSWORD_ID } from "./datameshIds";

const { SIGNIN } = screenNames;
const SigninForm: FC<SigninFormProps> = ({
  email,
  setEmail,
  closeScreen,
  onForgotPasswordClick,
}) => {
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const { state } = useScreenState();
  const gaTrackingPrefix = state.gaTrackingPrefix || state.trackingPrefix;

  const [emailValidationError, setEmailValidationError] = useState<string[]>(
    [],
  );
  const [password, setPassword] = useState("");
  const [passwordValidationError, setPasswordValidationError] = useState<
    string[]
  >([]);
  const [showError, setShowError] = useState(false);

  const { loading, mutate } = useEdoLogin({
    screen: SIGNIN,
    trackingPrefix: gaTrackingPrefix,
  });
  const onError = (): void => setShowError(true);
  const onSuccess = useCallback(
    ({ data }) =>
      data.passwordLogin.success === true ? closeScreen() : onError(),
    [closeScreen],
  );

  const onEnterSubmit = (): void => {
    document.getElementById(SIGNIN_SUBMIT_BUTTON)?.click();
  };

  const handleSubmit = useCallback(() => {
    setShowError(false);
    const { value: isValidEmail, errors: emailErrors } = validateEmail(email);
    const { value: isValidPassword, errors: passwordErrors } =
      validatePassword(password);

    if (!isValidEmail) setEmailValidationError(emailErrors);
    if (!isValidPassword) setPasswordValidationError(passwordErrors);
    if (!isValidEmail || !isValidPassword)
      trackError({
        trackEvent,
        isEmail: true,
        screen: SIGNIN,
        prefix: gaTrackingPrefix,
      });
    if (isValidEmail && isValidPassword)
      mutate(
        {
          email,
          password,
          acceptsNewsletter: state.userData.acceptsNewsletter,
        },
        { onSuccess, onError },
      );
  }, [
    email,
    password,
    trackEvent,
    gaTrackingPrefix,
    state.userData.acceptsNewsletter,
    mutate,
    onSuccess,
  ]);

  return (
    <Flex flexDirection="column" gap="1x_small">
      {showError && (
        <ModalMessage
          message={t("authentication.signin.error")}
          type="ERROR"
          trackingId={withPrefix(EDO_LOGIN_ERROR_ID, state.trackingPrefix)}
        />
      )}
      <AuthInputGroup
        email={email}
        setEmail={setEmail}
        emailValidationError={emailValidationError}
        setEmailValidationError={setEmailValidationError}
        password={password}
        setPassword={setPassword}
        passwordValidationError={passwordValidationError}
        setPasswordValidationError={setPasswordValidationError}
        loading={loading}
        onEnterSubmit={onEnterSubmit}
        trackingPrefix={state.trackingPrefix}
      />
      <Flex justifyContent="end" width="100%">
        <Box sx={{ textAlign: "end" }}>
          <FormLink
            trackingId={withPrefix(
              LOGIN_RESET_PASSWORD_ID,
              state.trackingPrefix,
            )}
            onClick={onForgotPasswordClick}
          >
            {t("authentication.signin.forgotPassword")}
          </FormLink>
        </Box>
      </Flex>
      <Box mt="1x_small">
        <SubmitButton loading={loading} onClick={handleSubmit} />
      </Box>
    </Flex>
  );
};

export default SigninForm;
